import PropTypes from 'prop-types'

import {
  Box,
  CoverImage,
  Icon,
  Heading,
  Link,
  Text,
} from '@redriverpress/system'

import getInitialState from 'Common/utils/getInitialState'

import FlashcardCategoryImagePlaceholder from './ImagePlaceholder'
import HitWrapper from './HitWrapper'
import Levels from './Levels'
import labels from '../../constants/labels'
import {
  lesson,
  post,
  flashcard,
  flashcardCategory,
  collection,
  video,
  studyPack,
  course,
  poll,
} from '../../constants/materialTypes'
import {
  collectionPropShape,
  flashcardCategoryPropShape,
  flashcardPropShape,
  lessonPropShape,
  postPropShape,
  videoPropShape,
  studyPackPropShape,
  coursePropShape,
  pollPropShape,
} from '../../constants/propShapes'
import {
  getCollectionLink,
  getCourseLink,
  getFlashcardLink,
  getLessonLink,
  getPollLink,
  getPostLink,
  getStudyPack,
  getVideoLink,
} from '../../utils/hitLinks'
import getHitLevelsByType from '../../utils/getHitLevelsByType'
import getPreferredLevelType from '../../utils/getPreferredLevelType'

const DISPLAY_LEVELS_FOR_TYPES = {
  [lesson]: true,
  [course]: true,
  [studyPack]: true,
  [post]: false,
  [flashcard]: false,
  [flashcardCategory]: false,
  [collection]: false,
  [video]: false,
  [poll]: false,
}

const getCoverImageBorderRadius = (type, hasImage) => {
  if (type === poll) return 'large'

  /**
   * Based on the designs:
   * if a collection has an image, the border radius must be circular.
   * if a collection doesn't have an image, the Category's image will be used instead, and the border radius must be rounded.
   */
  if (type === flashcard || (type === collection && !hasImage)) return 'small'

  return 'circular'
}

export default function AllMaterialsGridHit({ hit }) {
  const {
    author, // posts
    category, // flashcards
    lesson_section_slug: lessonSectionSlug, // lessons
    lesson_section_title: lessonSectionTitle, // lessons
    genre, // flashcards & flashcard categories
    id,
    image_url: imageUrl,
    is_resource: isResource,
    levels,
    slug,
    title,
    topic,
    subtitle,
    type,
    list_category_image_url: listCategoryImageUrl, // collections
    list_category_title: listCategoryTitle, // collections
    list_category_slug: listCategorySlug, // collections
  } = hit

  const { levelPreference, isUserLoggedIn } = getInitialState()

  const hitLevels = getHitLevelsByType(levelPreference, hit)

  // If the user's level preference isn't Ellii's,
  // but there are no levels on this hit for their level type,
  // use Ellii's levels.
  // Otherwise, use their level preference.
  const preferredLevelType = getPreferredLevelType(levelPreference, hitLevels)

  const linkByType = {
    [flashcard]: getFlashcardLink({
      genreSlug: genre?.slug,
      categorySlug: category?.slug,
      id,
    }),
    [flashcardCategory]: getFlashcardLink({
      genreSlug: genre?.slug,
      categorySlug: slug,
    }),
    [lesson]: getLessonLink({ lessonSectionSlug, slug, isUserLoggedIn }),
    [post]: getPostLink({ slug }),
    [collection]: getCollectionLink({ listCategorySlug, slug }),
    [video]: getVideoLink({ slug }),
    [studyPack]: getStudyPack({ lessonSectionSlug, slug }),
    [course]: getCourseLink({ lessonSectionSlug, slug }),
    [poll]: getPollLink({ slug }),
  }

  const secondaryInfo = {
    [flashcard]: category?.title,
    [lesson]: lessonSectionTitle,
    [post]: `by ${author}`,
    [collection]: listCategoryTitle,
    [studyPack]: category?.name,
    [course]: category?.name,
    [poll]: 'Ellii Polls',
  }

  const gridHitImageSize = ['3rem', null, '5rem']

  /**
   * Based on the designs:
   * if a collection has an image, the border radius must be circular.
   * if a collection doesn't have an image, the Category's image will be used instead, and the border radius must be rounded.
   */
  const borderRadius = getCoverImageBorderRadius(type, !!imageUrl)

  return (
    <Link reset href={linkByType[type]}>
      <HitWrapper display='flex' p={[2, null, 3]} height='100%'>
        <Box mr={[2, null, 3]}>
          {imageUrl || listCategoryImageUrl ? (
            <Box position='relative'>
              {type === video && (
                <Box position='absolute' size='100%'>
                  <Box
                    size={gridHitImageSize}
                    borderRadius='circular'
                    bg='rgba(35,31,32,0.5)'
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    mx={[undefined, null, 'auto']}
                  >
                    <Icon icon='play' weight='solid' color='white' />
                  </Box>
                </Box>
              )}
              <CoverImage
                src={imageUrl || listCategoryImageUrl}
                bg='transparent'
                alt=''
                size={gridHitImageSize}
                borderRadius={borderRadius}
              />
            </Box>
          ) : (
            <FlashcardCategoryImagePlaceholder size={gridHitImageSize} />
          )}
        </Box>
        <Box>
          <Text fontSize={0} color='gray700' mb={1} fontWeight='600'>
            {isResource ? labels.resource.text : labels[type].text}
          </Text>
          <Heading as='h5' mb={1}>
            {topic || title}
          </Heading>
          {type !== flashcardCategory && (
            <Text fontSize={0}>{secondaryInfo[type]}</Text>
          )}

          {DISPLAY_LEVELS_FOR_TYPES[type] ? (
            <Box my={2}>
              <Levels
                fontSize={0}
                eslLevels={levels}
                preferredLevels={hitLevels}
                preferredLevelType={preferredLevelType}
              />
            </Box>
          ) : null}

          {subtitle && type === video && (
            <Box display='flex' alignItems='baseline' my={2}>
              <Icon width='1rem' pr={2} flexShrink={0} icon='lightbulb' />
              <Text fontSize={0}>{subtitle}</Text>
            </Box>
          )}
        </Box>
      </HitWrapper>
    </Link>
  )
}

AllMaterialsGridHit.propTypes = {
  hit: PropTypes.oneOfType([
    collectionPropShape,
    flashcardCategoryPropShape,
    flashcardPropShape,
    lessonPropShape,
    postPropShape,
    videoPropShape,
    studyPackPropShape,
    coursePropShape,
    pollPropShape,
  ]).isRequired,
}
